import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  LinearProgress,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { enqueueSnackbar } from 'notistack';
import {
  type Comparison,
  type Granularity,
  useGetAccountPerformanceQuery,
} from 'src/context/api/campaignLeaks/api';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import Iconify from 'src/components/iconify';
import KpiCards, { KpiCardItem } from '../shared/kpi-cards';
import PlatformToggle, { useCampaignLeaksPlatform } from '../shared/platform-toggle';
import GridActions, { useGridPanels } from '../shared/grid-actions';
import { campaignLeaksGridProps } from '../shared/grid-config';
import { useCampaignLeaksRegions } from '../shared/regions';
import { applyFilters, applySort, isActiveFilter } from '../shared/grid-filtering';
import { useStoredColumns } from '../shared/use-stored-columns';
import { downloadCsv } from '../shared/csv';
import {
  DASH,
  formatConversions,
  formatRatio,
  formatDateRange,
  formatInteger,
  formatMoney,
} from '../shared/format';
import { EmptyState, ErrorState } from '../shared/states';
import { type CampaignLeaksT, useCampaignLeaksT } from '../shared/i18n';
import {
  CARD_SX,
  GRID_SX,
  ROW_H,
  TABLE_HEADER_SX,
  cappedGridHeight,
  rowsOnPage,
} from '../shared/grid-sx';
import PillSelect from './pill-select';
import { TOTAL_ID, buildPerformanceColumns } from './columns';
import {
  DEFAULT_GRANULARITY,
  DEFAULT_PERIOD,
  granularityOptions,
  equivalentPeriod,
  isPeriodDisabled,
  periodOptions,
} from './periods';
import {
  brandFilterOptions,
  BrandFilter,
  PerformanceRow,
  ZERO,
  applyBrandFilter,
  columnsStorageKey,
  defaultColumnVisibility,
  metricsFor,
  getPerformanceAccessors,
  performanceCsv,
  performanceKpis,
  templateOf,
  toGridRows,
} from './utils';

const comparisonOptions = (ct: CampaignLeaksT): { value: Comparison; label: string }[] => [
  { value: 'previous_period', label: ct('toolbar.previous_period') },
  { value: 'none', label: ct('toolbar.no_comparison') },
];

const DEFAULT_SORT: GridSortModel = [{ field: 'ad_cost', sort: 'desc' }];

export const performanceKpiItems = (
  kpis: ReturnType<typeof performanceKpis>,
  currency: string,
  ct: CampaignLeaksT,
  template: 'lead' | 'ecommerce'
): KpiCardItem[] => [
  // Definitions of the cards' own scope: Brand and Non brand campaigns together.
  {
    key: 'ad_cost',
    title: ct('kpis.perf_ad_cost.title'),
    definition: ct('kpis.perf_ad_cost.definition'),
    value: formatMoney(kpis.ad_cost, currency),
  },
  {
    key: 'ad_clicks',
    title: ct('kpis.perf_ad_clicks.title'),
    definition: ct('kpis.perf_ad_clicks.definition'),
    value: formatInteger(kpis.ad_clicks),
  },
  // Lead: Paid Conversions and Paid CPA; E-commerce: Paid Revenue and Paid ROAS.
  ...(template === 'lead'
    ? [
        {
          key: 'paid_conversions',
          title: ct('kpis.perf_paid_conversions.title'),
          definition: ct('kpis.perf_paid_conversions.definition'),
          value: formatConversions(kpis.paid_conversions),
        },
        {
          key: 'paid_cpa',
          title: ct('kpis.perf_paid_cpa.title'),
          definition: ct('kpis.perf_paid_cpa.definition'),
          value: kpis.paid_cpa === null ? DASH : formatMoney(kpis.paid_cpa, currency),
        },
      ]
    : [
        {
          key: 'paid_revenue',
          title: ct('kpis.perf_paid_revenue.title'),
          definition: ct('kpis.perf_paid_revenue.definition'),
          value: formatMoney(kpis.paid_revenue, currency),
        },
        {
          key: 'paid_roas',
          title: ct('kpis.perf_paid_roas.title'),
          definition: ct('kpis.perf_paid_roas.definition'),
          value: kpis.paid_roas === null ? DASH : formatRatio(kpis.paid_roas),
        },
      ]),
];

/** Same switch as the Business Overview table: % evolution ↔ absolute difference. */
function EvolutionToggle({ percent, onToggle }: { percent: boolean; onToggle: () => void }) {
  const ct = useCampaignLeaksT();
  return (
    <Tooltip
      title={
        percent ? ct('toolbar.showing_percent') : ct('toolbar.showing_absolute')
      }
      placement="top"
    >
      <ButtonBase
        onClick={onToggle}
        // The label says the current mode and what a click does (same text as the tooltip).
        aria-label={percent ? ct('toolbar.showing_percent') : ct('toolbar.showing_absolute')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          px: '6px',
          py: '4px',
          borderRadius: '6px',
          bgcolor: percent ? 'rgba(145,158,171,0.08)' : 'transparent',
          color: '#1e88e5',
        }}
      >
        <Iconify
          icon={percent ? 'material-symbols:percent-rounded' : 'material-symbols:tag-rounded'}
          width={16}
          sx={{ color: 'inherit' }}
        />
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            lineHeight: '16.8px',
            color: 'inherit',
            whiteSpace: 'nowrap',
            mx: '2px',
          }}
        >
          {ct('toolbar.evolution')}
        </Typography>
        <Iconify icon="mi:switch" width={16} sx={{ color: 'inherit' }} />
      </ButtonBase>
    </Tooltip>
  );
}

export default function AccountPerformance() {
  const workspace: any = useCurrentWorkspace();
  const platform = useCampaignLeaksPlatform();
  const { regionsParam } = useCampaignLeaksRegions();
  const currency = workspace?.currency || 'USD';
  const template = templateOf(workspace?.template);
  const metrics = useMemo(() => metricsFor(template), [template]);
  // Read again on every opening of the period menu: a tab left open overnight gets today's options.
  const [today, setToday] = useState(() => new Date());
  const ct = useCampaignLeaksT();
  const performanceAccessors = useMemo(() => getPerformanceAccessors(ct), [ct]);

  const [granularity, setGranularity] = useState<Granularity>(DEFAULT_GRANULARITY);
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [comparison, setComparison] = useState<Comparison>('previous_period');
  const [percent, setPercent] = useState(true);
  const [brandFilter, setBrandFilter] = useState<BrandFilter>('all');

  // `data` keeps the previous result while a new period / platform loads.
  const { data, error, isLoading, isFetching, isError, refetch } = useGetAccountPerformanceQuery(
    { client: workspace?.bqTable, platform, granularity, period, comparison, regions: regionsParam },
    { skip: !workspace?.bqTable }
  );
  // « This … » has no data yet (first day of the unit, page opened the day before): back to « Last … ».
  const periodEmpty = (error as any)?.status === 400 && (error as any)?.data?.error === 'period_empty';
  useEffect(() => {
    if (!periodEmpty || !period.startsWith('this_')) return;
    setToday(new Date());
    setPeriod(equivalentPeriod(period, granularity));
    enqueueSnackbar(ct('toolbar.period_empty'), { variant: 'info' });
  }, [periodEmpty, period, granularity, ct]);
  const hasCompare = comparison !== 'none' && !!data?.compare;

  const apiRef = useRef<any>(null);
  const { onButtonDown, onToggle, panelSlotProps } = useGridPanels(apiRef);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const defaultModel = useMemo(() => defaultColumnVisibility(template), [template]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useStoredColumns(
    columnsStorageKey(template),
    defaultModel
  );

  // Back to the first page on every sort, filter, period, platform or region change.
  const rowsKey = JSON.stringify({
    items: filterModel.items,
    sortModel,
    brandFilter,
    platform,
    granularity,
    period,
    comparison,
    regionsParam,
  });
  const [paginatedKey, setPaginatedKey] = useState(rowsKey);
  if (paginatedKey !== rowsKey) {
    setPaginatedKey(rowsKey);
    setPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
  }

  const allRows = useMemo(() => toGridRows(data?.rows), [data]);
  // Brand filter of the toolbar + column filters, client-side: no new API call.
  const rows = useMemo(
    () =>
      applySort(
        applyFilters(
          applyBrandFilter(allRows, brandFilter),
          filterModel.items,
          performanceAccessors
        ),
        sortModel,
        performanceAccessors
      ),
    [allRows, brandFilter, filterModel.items, sortModel, performanceAccessors]
  );
  // TOTAL = account total, whatever the Brand filter (decision 1).
  const total = useMemo(
    () => ({
      current: data?.account_total?.current ?? ZERO,
      compare: hasCompare ? (data?.account_total?.compare ?? null) : null,
    }),
    [data, hasCompare]
  );
  const totalRow: PerformanceRow = {
    id: TOTAL_ID,
    campaign_type: 'TOTAL',
    is_brand: false,
    ...total,
  };
  const displayedRows = useMemo(
    () => (hasCompare ? rows : rows.map((row) => ({ ...row, compare: null }))),
    [rows, hasCompare]
  );

  const columns = useMemo(
    () =>
      buildPerformanceColumns({
        ct,
        metrics,
        rows,
        total,
        currency,
        hasCompare,
        percent,
        partialCoverage: (data?.compare_coverage ?? 1) < 1,
        campaignTypes: Array.from(new Set(allRows.map((row) => row.campaign_type))).sort(),
      }),
    [ct, metrics, rows, total, currency, hasCompare, percent, data?.compare_coverage, allRows]
  );

  // No dates while the new period loads: `data` still holds the previous period.
  const periodTooltip = data && !isFetching
    ? [
        ct('toolbar.period_dates', { dates: formatDateRange(data.period.start, data.period.end) }),
        data.compare
          ? ct('toolbar.compare_dates', { dates: formatDateRange(data.compare.start, data.compare.end) })
          : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  const handleGranularity = (next: string) => {
    const g = next as Granularity;
    setGranularity(g);
    setPeriod(equivalentPeriod(period, g));
  };

  const handleExport = () => {
    const { fields, data: csv } = performanceCsv(rows, total, template, hasCompare, ct);
    downloadCsv(
      `campaign-leaks_account-performance_${workspace?.bqTable ?? ''}_${platform}_${data?.period.end ?? ''}.csv`,
      csv,
      fields
    );
  };

  const renderBody = () => {
    if (isError && !periodEmpty) return <ErrorState onRetry={refetch} />;
    if (isLoading || !data) return <Skeleton variant="rectangular" height={ROW_H * 7} />;
    if (!allRows.length) return <EmptyState message={ct('states.no_data_period')} />;
    const cappedHeight = cappedGridHeight(
      rowsOnPage(displayedRows.length, paginationModel.page, paginationModel.pageSize),
      1
    );
    return (
      <Box sx={{ height: cappedHeight ?? 'auto' }} data-testid="grid-box">
        <DataGridPro
          {...campaignLeaksGridProps({ apiRef, panelSlotProps, defaultModel })}
          autoHeight={cappedHeight === null}
          rows={displayedRows}
          pinnedRows={{ bottom: [totalRow] }}
          columns={columns}
          getRowClassName={({ id }) => (id === TOTAL_ID ? 'campaign-leaks-total-row' : '')}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model.length ? model : DEFAULT_SORT)}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sx={{
            ...GRID_SX,
            '& .campaign-leaks-total-row, & .campaign-leaks-total-row:hover': {
              backgroundColor: 'rgba(145, 158, 171, 0.08)',
            },
          }}
        />
      </Box>
    );
  };

  return (
    <Stack spacing={3} sx={{ px: '24px', pb: 3, fontFamily: 'Public Sans' }}>
      <KpiCards
        items={performanceKpiItems(performanceKpis(rows), currency, ct, template)}
        loading={isLoading || (!data && !isError)}
        error={isError && !periodEmpty}
      />
      <Box sx={{ ...CARD_SX, position: 'relative' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={TABLE_HEADER_SX}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <PillSelect
              label={ct('toolbar.granularity')}
              value={granularity}
              options={granularityOptions(ct)}
              onChange={handleGranularity}
            />
            <PillSelect
              label={ct('toolbar.period')}
              value={period}
              options={periodOptions(granularity, ct).map((option) => ({
                ...option,
                disabled: isPeriodDisabled(granularity, option.value, today),
                disabledTooltip: ct('toolbar.no_data_yet'),
              }))}
              onChange={setPeriod}
              onOpen={() => setToday(new Date())}
            />
            <PillSelect
              label={ct('toolbar.comparison')}
              value={comparison}
              options={comparisonOptions(ct)}
              onChange={(value) => setComparison(value as Comparison)}
              tooltip={periodTooltip}
            />
          </Stack>
          {comparison !== 'none' && (
            <EvolutionToggle percent={percent} onToggle={() => setPercent((value) => !value)} />
          )}
          <PlatformToggle />
          <PillSelect
            label={ct('toolbar.brand_filter')}
            value={brandFilter}
            options={brandFilterOptions(ct)}
            onChange={(value) => setBrandFilter(value as BrandFilter)}
          />
          <GridActions
            activeFilters={filterModel.items.filter(isActiveFilter).length}
            onButtonDown={onButtonDown}
            onToggle={onToggle}
            onExport={handleExport}
            exportDisabled={!data || !rows.length}
          />
        </Stack>
        {isFetching && !isLoading && (
          <LinearProgress
            data-testid="refetch-indicator"
            sx={{ position: 'absolute', left: 0, right: 0, top: 40, height: 2 }}
          />
        )}
        {renderBody()}
      </Box>
    </Stack>
  );
}
